import { ArrowLeft, Wind, Info, Watch } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { useNavigate } from "react-router-dom";

const BreathingRateDetail = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/fitness")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">Breathing Rate</h1>
        </div>

        {/* Sleep Tracking Notice */}
        <Card className="border-warning/50 bg-warning/10 rounded-xl border-2 p-4">
          <div className="flex items-start gap-3">
            <div className="bg-warning/20 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg">
              <Watch className="text-warning h-5 w-5" />
            </div>
            <div>
              <h4 className="mb-1 text-sm font-semibold text-foreground">
                Sleep Tracking Required
              </h4>
              <p className="text-xs text-muted-foreground">
                Breathing rate is recorded only when you wear your tracker watch
                while sleeping. Make sure to wear it overnight for accurate
                readings.
              </p>
            </div>
          </div>
        </Card>

        {/* Current Breathing Rate */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="bg-chart-2/10 flex h-16 w-16 items-center justify-center rounded-xl">
              <Wind className="text-chart-2 h-8 w-8" />
            </div>
            <div>
              <p className="mb-1 text-sm text-muted-foreground">Average Rate</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-foreground">14</span>
                <span className="text-lg text-muted-foreground">
                  breaths/min
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* What is Breathing Rate */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Info className="text-info h-5 w-5" />
            <h3 className="font-semibold text-foreground">
              What is Breathing Rate?
            </h3>
          </div>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">Breathing rate</strong> is the
              number of breaths you take per minute. It's measured during sleep
              when your body is at rest, providing insight into your respiratory
              health.
            </p>
            <p>
              <strong className="text-foreground">During sleep</strong>, your
              breathing rate naturally decreases compared to when you're awake.
              A consistent breathing rate indicates stable respiratory function.
            </p>
            <p>
              <strong className="text-foreground">
                Changes in breathing rate
              </strong>{" "}
              can indicate stress, illness, or the onset of respiratory
              conditions. Tracking this metric over time helps identify patterns
              and potential health concerns.
            </p>
          </div>
        </Card>

        {/* Factors That Affect Breathing Rate */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-foreground">
            Factors That Affect Breathing Rate
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-chart-2">•</span>
              <span>Physical fitness level</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-2">•</span>
              <span>Stress and anxiety</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-2">•</span>
              <span>Illness or infection</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-2">•</span>
              <span>Room temperature and altitude</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-2">•</span>
              <span>Medications</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default BreathingRateDetail;
